import React, { useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import CPPUtils from 'services/CPPUtils';
import View from './view';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useReduxForm } from 'hooks/useReduxForm';
import { useFormState } from 'react-hook-form';
import { SectionConfig } from './configs';
import { Patient } from '../../../../../../generated/api';

function buildSchema(config: SectionConfig) {
  const shape: Record<string, z.ZodTypeAny> = {
    itemId: z.any().optional(),
  };

  for (const fieldName of config.schemaFields) {
    const fieldCfg = config.fields.find((f) => f.name === fieldName);
    const isRequired = fieldCfg?.required;

    if (isRequired) {
      shape[fieldName] = z
        .string()
        .min(1, `Must input a ${fieldCfg?.label?.toLowerCase() || fieldName}!`);
    } else {
      shape[fieldName] = z.string().optional().or(z.literal(''));
    }
  }

  if (config.hiddenValues) {
    for (const key of Object.keys(config.hiddenValues)) {
      if (!shape[key]) {
        shape[key] = z.string().optional();
      }
    }
  }

  return z.object(shape);
}

const CPPSectionInput = ({
  config,
  patient,
  ...rest
}: {
  config: SectionConfig;
  patient: Patient;
  [key: string]: any;
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const schema = React.useMemo(() => buildSchema(config), [config]);

  const formNameSuffix =
    config.api.type === 'history' ? config.hiddenValues?.history_type : undefined;
  const formName = CPPUtils.getFormName(
    config.formKey || config.api.field || config.title,
    patient.id!,
    formNameSuffix,
  );

  const defaultValues = {
    ...config.defaultValues,
    itemId: undefined,
  };

  const { control, handleSubmit, setValue, watch, reset } = useReduxForm(formName, {
    destroyOnUnmount: false,
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onChange',
  });

  const { isValid } = useFormState({ control });

  const itemId = watch('itemId');
  const name = watch('name') as string;
  const isEditing = itemId !== undefined;

  const onCustomButtonClick = (presetName: string) => {
    setValue('name', presetName, { shouldValidate: true });
  };

  const onSave = async (values: any) => {
    const value: Record<string, any> = {};
    for (const fieldName of config.schemaFields) {
      if (values[fieldName] !== undefined) {
        value[fieldName] = values[fieldName];
      }
    }
    if (config.hiddenValues) {
      Object.assign(value, config.hiddenValues);
    }

    const item: any = { id: itemId, value };
    setLoading(true);

    let result: any;
    switch (config.api.type) {
      case 'patientIssue':
        result = await dispatch(
          api.HealthRecords.addOrEditHealthRecordPatientIssue({
            patientId: patient.id!,
            field: config.api.field as any,
            patientIssues: [item],
          }),
          { errorTitle: config.title },
        );
        break;
      case 'history':
        result = await dispatch(
          api.HealthRecords.addOrEditHealthRecordHistoryIssue({
            patientId: patient.id!,
            historyIssues: [item],
          }),
        );
        break;
      case 'lifestyle':
        result = await dispatch(
          api.HealthRecords.addOrEditHealthRecordLifestyleFactor({
            patientId: patient.id!,
            lifestyleFactors: [item],
          }),
          { errorTitle: config.title },
        );
        break;
      case 'consultant':
        result = await dispatch(
          api.HealthRecords.addOrEditHealthRecordConsultant({
            patientId: patient.id!,
            consultants: [item],
          }),
          { errorTitle: config.title },
        );
        break;
    }

    setLoading(false);
    if (result) reset();
  };

  const onDeleteItem = () => {
    setShowConfirmationDialog(true);
  };

  const confirmDelete = async (values: any) => {
    setLoading(true);
    let result: any;

    switch (config.api.type) {
      case 'patientIssue':
        result = await dispatch(
          api.HealthRecords.deleteHealthRecordPatientIssue({
            patientId: patient.id!,
            field: config.api.field as any,
            issueId: values.itemId,
          }),
          { errorTitle: config.title },
        );
        break;
      case 'history':
        result = await dispatch(
          api.HealthRecords.deleteHealthRecordHistoryIssue({
            patientId: patient.id!,
            issueId: values.itemId,
          }),
        );
        break;
      case 'lifestyle':
        result = await dispatch(
          api.HealthRecords.deleteHealthRecordLifestyleFactor({
            patientId: patient.id!,
            factorId: values.itemId,
          }),
          { errorTitle: config.title },
        );
        break;
      case 'consultant':
        result = await dispatch(
          api.HealthRecords.deleteHealthRecordConsultant({
            patientId: patient.id!,
            consultantId: values.itemId,
          }),
          { errorTitle: config.title },
        );
        break;
    }

    setShowConfirmationDialog(false);
    setLoading(false);
    if (result) reset();
  };

  const clearForm = () => {
    reset();
  };

  const onPackYearResult = (value: number) => {
    setValue('details', value + ' pack-year');
  };

  return (
    <View
      {...rest}
      config={config}
      patient={patient}
      control={control}
      name={name}
      isEditing={isEditing}
      isValid={isValid}
      loading={loading}
      onSave={handleSubmit(onSave)}
      onDeleteItem={onDeleteItem}
      confirmDelete={handleSubmit(confirmDelete)}
      onCustomButtonClick={onCustomButtonClick}
      clearForm={clearForm}
      showConfirmationDialog={showConfirmationDialog}
      setShowConfirmationDialog={setShowConfirmationDialog}
      onPackYearResult={onPackYearResult}
    />
  );
};

export default CPPSectionInput;
